import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Mail, Flame, Trophy, Rocket, Zap, Crown } from "lucide-react";
import EUSaudiBanner from "@/components/EUSaudiBanner";

const ICONS = { rocket: Rocket, zap: Zap, flame: Flame, trophy: Trophy };

const StatCard = ({ label, value, accent, testid }) => (
  <div className="border border-black/10 bg-white p-6 hover:bg-gray-50 transition-colors" data-testid={testid}>
    <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-3">{label}</div>
    <div className={`font-display font-black text-4xl md:text-5xl tracking-tighter ${accent || ""}`}>{value}</div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [stats, setStats] = useState({ total: 0, sent: 0, viewed: 0, responded: 0, rejected: 0, unread_messages: 0 });
  const [recent, setRecent] = useState([]);
  const [usage, setUsage] = useState(null);
  const [salary, setSalary] = useState(null);
  const [nitaqat, setNitaqat] = useState(null);

  useEffect(() => {
    api.get("/applications/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/applications").then((r) => setRecent(r.data.slice(0, 5))).catch(() => {});
    api.get("/usage").then((r) => setUsage(r.data)).catch(() => {});
    api.get("/intel/salary").then((r) => setSalary(r.data)).catch(() => {});
    api.get("/intel/nitaqat").then((r) => setNitaqat(r.data)).catch(() => {});
  }, []);

  const usagePct = usage ? Math.min(100, (usage.used / Math.max(usage.total_limit, 1)) * 100) : 0;

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      {/* Surfaces to EU candidates who haven't yet opted into KSA hire visibility.
          Self-hides if not applicable. */}
      <EUSaudiBanner />

      <div>
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-gray-500 mb-3 flex items-center justify-between flex-wrap gap-2">
          <span>/ {t("dashboard")}</span>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/candidates`}
            target="_blank" rel="noopener noreferrer"
            className="font-mono text-[10px] uppercase tracking-[0.3em] text-gray-500 hover:text-[#FF9500] transition-colors"
            data-testid="dashboard-download-doc"
          >
            📄 What is Maqam? (PDF)
          </a>
        </div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">
          {t("welcome_back")}, <span className="text-gradient">{user?.full_name?.split(" ")[0]}</span>.
        </h1>
      </div>

      {/* Usage + Streak row */}
      {usage && (
        <div className="grid md:grid-cols-[2fr_1fr] gap-6">
          <div className="border border-black/10 bg-[#F8F9FA] p-6 md:p-8" data-testid="usage-widget">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Your plan</div>
                <div className="font-display font-black text-2xl uppercase flex items-center gap-2">
                  <Crown className="w-5 h-5 text-[#FF3B30]" />
                  {usage.plan}
                </div>
              </div>
              <Link
                to="/app/billing"
                className="font-mono text-xs uppercase tracking-widest bg-black text-white px-4 py-2 hover:bg-[#FF3B30] transition-colors"
                data-testid="upgrade-link"
              >
                {usage.plan === "pro" ? "Manage" : "Upgrade →"}
              </Link>
            </div>
            <div className="flex justify-between font-mono text-xs mb-2">
              <span>CV sends used</span>
              <span>
                <span className="num-ticker font-bold text-black">{usage.used}</span> / {usage.total_limit}
              </span>
            </div>
            <div className="w-full h-3 bg-white border border-black/10">
              <div
                className={`h-full ${usagePct >= 100 ? "bg-[#FF3B30]" : "bg-[#0033FF]"}`}
                style={{ width: `${usagePct}%` }}
              />
            </div>
            {usage.bonus_sends > 0 && (
              <div className="mt-2 text-xs font-mono text-[#34C759]">
                +{usage.bonus_sends} bonus from referrals
              </div>
            )}
          </div>

          <div className="border border-black/10 bg-black text-white p-6 md:p-8 flex flex-col justify-between" data-testid="streak-widget">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1">Streak</div>
                <div className="num-ticker font-display font-black text-6xl tracking-tighter text-[#FF3B30]">
                  {usage.streak_days}
                </div>
                <div className="text-xs text-gray-400 uppercase tracking-widest mt-1">days applying</div>
              </div>
              <Flame className="w-16 h-16 text-[#FF3B30]" />
            </div>
            {usage.badges?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-5">
                {usage.badges.map((b) => {
                  const I = ICONS[b.icon] || Trophy;
                  return (
                    <span key={b.id} className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 px-2 py-1 text-[10px] font-mono uppercase tracking-widest">
                      <I className="w-3 h-3" /> {b.label}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quick stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard label={t("total_apps")} value={stats.total} testid="stat-total" />
        <StatCard label={t("sent")} value={stats.sent || 0} accent="text-[#0033FF]" testid="stat-sent" />
        <StatCard label={t("viewed")} value={stats.viewed || 0} testid="stat-viewed" />
        <StatCard label={t("responded")} value={stats.responded || 0} accent="text-[#34C759]" testid="stat-responded" />
        <StatCard label={t("unread")} value={stats.unread_messages || 0} accent="text-[#FF3B30]" testid="stat-unread" />
      </div>

      {/* CTA grid */}
      <div className="grid md:grid-cols-3 gap-6">
        <Link
          to="/app/search"
          className="md:col-span-2 relative overflow-hidden bg-black text-white p-10 group hover:bg-[#0033FF] transition-colors"
          data-testid="cta-search"
        >
          <Sparkles className="w-8 h-8 mb-8 stroke-[1.5]" />
          <h3 className="font-display font-black text-3xl md:text-4xl tracking-tighter mb-3">{t("quick_search")}</h3>
          <p className="text-gray-300 max-w-lg mb-8">
            Type a job role, pick your cities, let our matching engine rank postings across the Kingdom in seconds.
          </p>
          <div className="inline-flex items-center gap-2 font-bold text-sm border-b-2 border-white pb-1 group-hover:gap-4 transition-all">
            Start search <ArrowRight className="w-4 h-4" />
          </div>
        </Link>

        <Link
          to="/app/inbox"
          className="bg-white border border-black/10 p-10 hover:bg-gray-50 transition-colors"
          data-testid="cta-inbox"
        >
          <Mail className="w-8 h-8 mb-8 stroke-[1.5]" />
          <h3 className="font-display font-black text-2xl tracking-tighter mb-3">{t("inbox")}</h3>
          <p className="text-gray-600 text-sm mb-4">
            Your platform inbox: <span className="font-mono text-[#0033FF]">{user?.platform_email}</span>
          </p>
          {stats.unread_messages > 0 ? (
            <div className="text-[#FF3B30] font-bold">{stats.unread_messages} unread</div>
          ) : (
            <div className="text-gray-500 text-sm">All caught up</div>
          )}
        </Link>
      </div>

      {/* Salary Intel teaser */}
      {salary && salary.sample_size > 0 && (
        <Link to="/app/salary" className="block border border-black/10 bg-[#F8F9FA] p-8 hover:bg-white transition-colors" data-testid="salary-teaser">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div>
              <div className="font-mono text-xs uppercase tracking-widest text-gray-500 mb-2">Saudi market salary (all roles)</div>
              <div className="flex items-baseline gap-3">
                <span className="num-ticker font-display font-black text-4xl tracking-tighter">
                  SAR {salary.median.toLocaleString()}
                </span>
                <span className="text-gray-500 text-sm">median</span>
              </div>
              <div className="text-sm text-gray-600 mt-1">
                Range: SAR {salary.min.toLocaleString()} — SAR {salary.max.toLocaleString()} • {salary.sample_size} live postings
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm font-bold link-underline">
              Explore salary intel <ArrowRight className="w-4 h-4" />
            </div>
          </div>
        </Link>
      )}

      {/* Nitaqat Score */}
      {nitaqat && (
        <div className="grid md:grid-cols-[1fr_2fr] gap-0 border border-black/10" data-testid="nitaqat-widget">
          <div className={`p-8 flex flex-col justify-center ${
            nitaqat.tier === "Platinum" ? "bg-black text-white"
            : nitaqat.tier === "Gold" ? "bg-[#F4B400] text-black"
            : nitaqat.tier === "Silver" ? "bg-[#E5E5E5] text-black"
            : "bg-[#F8F9FA]"
          }`}>
            <div className="font-mono text-[10px] uppercase tracking-widest opacity-70 mb-2">Nitaqat Match</div>
            <div className="num-ticker font-display font-black text-7xl tracking-tighter leading-none">
              {nitaqat.score}
            </div>
            <div className="font-display font-bold uppercase tracking-widest mt-2 text-sm">
              {nitaqat.tier}
            </div>
          </div>
          <div className="p-8 bg-white">
            <h3 className="font-display font-bold text-lg mb-2">Your Saudization edge</h3>
            <p className="text-sm text-gray-700 mb-5">{nitaqat.narrative}</p>
            <div className="space-y-2">
              {nitaqat.breakdown.map((r, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className={r.positive ? "text-gray-800" : "text-gray-500"}>{r.label}</span>
                  <span className={`font-mono font-bold ${r.positive ? "text-[#34C759]" : "text-gray-400"}`}>
                    {r.positive ? "+" : ""}{r.value}
                  </span>
                </div>
              ))}
            </div>
            <Link to="/app/profile" className="inline-block mt-5 font-mono text-xs uppercase tracking-widest link-underline">
              Update profile to improve →
            </Link>
          </div>
        </div>
      )}

      {/* Recent apps */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display font-bold text-2xl tracking-tight">{t("recent_apps")}</h2>
          <Link to="/app/applications" className="font-mono text-xs uppercase tracking-widest link-underline">
            {t("view_all")}
          </Link>
        </div>
        {recent.length === 0 ? (
          <div className="border border-dashed border-black/20 p-10 text-center text-gray-500" data-testid="dash-no-apps">
            {t("no_data")} — run your first search.
          </div>
        ) : (
          <div className="border border-black/10 divide-y divide-black/10" data-testid="dash-recent-list">
            {recent.map((a) => (
              <div key={a.id} className="flex items-center justify-between p-5 hover:bg-gray-50">
                <div>
                  <div className="font-display font-bold">{a.job_title}</div>
                  <div className="text-sm text-gray-600">
                    {a.company} • {a.city}
                  </div>
                </div>
                <div className="font-mono text-xs uppercase tracking-widest text-[#0033FF]">{a.status}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
